/**
 * Parses CSV text and returns an array of objects
 * @param {string} csvText - The CSV file content as text
 * @param {number} maxRows - Maximum number of rows to parse (excluding header)
 * @returns {Array} Array of objects where keys are column headers
 */
const parseCSV = (csvText, maxRows = null) => {
    const lines = csvText.split('\n').filter(line => line.trim() !== '');

    if (lines.length === 0) {
        return [];
    }

    // Parse CSV line considering quoted values
    const parseLine = (line) => {
        const result = [];
        let currentValue = '';
        let insideQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"') {
                if (insideQuotes && nextChar === '"') {
                    // Escaped quote
                    currentValue += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    insideQuotes = !insideQuotes;
                }
            } else if (char === ',' && !insideQuotes) {
                // End of field
                result.push(currentValue.trim());
                currentValue = '';
            } else {
                currentValue += char;
            }
        }

        // Push the last value
        result.push(currentValue.trim());
        return result;
    };

    // Get headers from first line
    const headers = parseLine(lines[0]);

    // Parse data rows
    const rowLimit = maxRows ? Math.min(maxRows + 1, lines.length) : lines.length;
    const dataRows = [];

    for (let i = 1; i < rowLimit; i++) {
        const values = parseLine(lines[i]);
        const rowObject = {};

        headers.forEach((header, index) => {
            rowObject[header] = values[index] || '';
        });

        dataRows.push(rowObject);
    }

    return dataRows;
};

/**
 * Handles file upload and extracts first 5 rows for preview
 * @param {File} file - The uploaded CSV file
 * @returns {Promise<Object>} Object containing preview data and the file
 */
export const handleFileUpload = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error('No file provided'));
            return;
        }

        // Validate file type
        const validTypes = ['text/csv', 'application/vnd.ms-excel', 'text/plain'];
        const isCSV = validTypes.includes(file.type) || file.name.endsWith('.csv');

        if (!isCSV) {
            reject(new Error('Invalid file type. Please upload a CSV file.'));
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const csvText = event.target.result;

                // Calculate total rows (excluding header)
                const totalRows = csvText.split('\n').filter(line => line.trim() !== '').length - 1;

                // Parse first 5 rows for preview
                const previewData = parseCSV(csvText, 5);

                if (previewData.length === 0) {
                    reject(new Error('CSV file is empty or invalid'));
                    return;
                }

                // Get headers
                const headers = Object.keys(previewData[0]);

                resolve({
                    previewData: previewData,
                    headers: headers,
                    file: file,
                    fileName: file.name,
                    fileSize: file.size,
                    totalRows: totalRows // Return total rows for validation in Step2FileUpload
                });
            } catch (error) {
                reject(new Error(`Failed to parse CSV: ${error.message}`));
            }
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        // Read file as text
        reader.readAsText(file);
    });
};

/**
 * Validates the uploaded file
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 10MB)
 * @returns {Object} Validation result with isValid and error message
 */
export const validateFile = (file, options = {}) => {
    const { maxSizeMB = 10 } = options;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (!file) {
        return {
            isValid: false,
            error: 'No file selected'
        };
    }

    if (file.size > maxSizeBytes) {
        return {
            isValid: false,
            error: `File size exceeds ${maxSizeMB}MB limit`
        };
    }

    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'text/plain'];
    const isCSV = validTypes.includes(file.type) || file.name.endsWith('.csv');

    if (!isCSV) {
        return {
            isValid: false,
            error: 'Invalid file type. Only CSV files are allowed'
        };
    }

    return {
        isValid: true,
        error: null
    };
};

/**
 * Formats file size to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};
